import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ClassroomGuide from './components/ClassroomGuide';
import ArtworkGallery from './components/ArtworkGallery';
import './App.css';

const {
  FiArrowUpRight,
  FiArrowRight,
  FiMenu,
  FiX,
  FiStar,
  FiCompass,
  FiHeart,
  FiSmile,
  FiBookOpen,
  FiCheckCircle
} = FiIcons;

const base = 'https://cloud.sharemyimage.com/2026/10/08/';
const amazonUrl = 'https://www.amazon.sg/Binjamin-Dumpster-David-Smith/dp/B0BSRPSDWL';

const pepitoImages = {
  cover: 'https://cloud.sharemyimage.com/2026/10/09/Pepito_HermitCrab1.jpg',
  bldg: 'https://cloud.sharemyimage.com/2026/10/09/PepitoHermitCrab_bldg.png',
  exclamation: 'https://cloud.sharemyimage.com/2026/10/09/PepitoHermitCrab_exclamation.png',
  pipes: 'https://cloud.sharemyimage.com/2026/10/09/PepitoHermitCrab_pipes.png',
  bricks: 'https://cloud.sharemyimage.com/2026/10/09/PepitoHermitCrab_bricks.png'
};

const coreThemes = [
  {
    icon: FiCompass,
    title: 'Finding Your Life Purpose',
    desc: 'Encouraging young readers to listen to their inner calling, wonder "what could be," and explore what makes them truly special.'
  },
  {
    icon: FiHeart,
    title: 'Overcoming Trauma & Hardship',
    desc: 'Gentle, supportive storytelling that helps children process difficult moments with empathy, courage, and hope.'
  },
  {
    icon: FiSmile,
    title: "Navigating Life's Twists & Turns",
    desc: 'Embracing unexpected change and learning resilience through humor, companionship, and heartwarming adventures.'
  }
];

const characterFaces = [
  `${base}Binjamin_Faces_1.png`,
  `${base}Binjamin_Faces_2.png`,
  `${base}Binjamin_Faces_3.png`,
  `${base}Binjamin_Faces_4.png`,
  `${base}Binjamin_Faces_5.png`,
  `${base}Binjamin_Faces_6.png`,
  `${base}Binjamin_Faces_7.png`,
  `${base}Binjamin_Faces_8.png`
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <a className="brand" href="#home" aria-label="David Smith Children's Books, back to top">
        <img
          src={`${base}Binjamin_Faces_1.png`}
          alt="Binjamin character icon"
          className="brand-avatar"
        />
        <div className="brand-text-block">
          <span className="brand-book-title">DAVID SMITH</span>
          <span className="brand-author-sub">BINJAMIN & PEPITO THE HERMIT CRAB</span>
        </div>
      </a>

      <button
        className="menu-toggle"
        type="button"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <SafeIcon icon={open ? FiX : FiMenu} />
      </button>

      <nav className={open ? 'nav-links open' : 'nav-links'} aria-label="Main navigation">
        <a href="#blurb" onClick={() => setOpen(false)}>The Story</a>
        <a href="#themes" onClick={() => setOpen(false)}>Themes</a>
        <a href="#books" onClick={() => setOpen(false)}>Books</a>
        <a href="#guide" onClick={() => setOpen(false)}>Discussion Guide</a>
        <a href="#illustrations" onClick={() => setOpen(false)}>Artwork</a>
        <a href="#about" onClick={() => setOpen(false)}>David Smith</a>
        <a
          className="nav-cta"
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Buy on Amazon <SafeIcon icon={FiArrowUpRight} />
        </a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-copy">
        <div className="eyebrow">
          <span className="eyebrow-line" /> A HEARTWARMING WORLD OF PURPOSE & WONDER
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65 }}
        >
          A little <em>imagination</em> goes a long way<span className="period">.</span>
        </motion.h1>

        <p>
          Meet Binjamin the Dumpster and Pepito the Hermit Crab: heartwarming characters who dare
          to ask what else is possible! Step into David Smith’s imaginative stories exploring courage,
          adaptation, and life’s adventures.
        </p>

        <div className="hero-actions">
          <a
            className="button button-dark"
            href={amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Order Binjamin <SafeIcon icon={FiArrowUpRight} />
          </a>
          <a className="text-link" href="#books">
            Explore Both Books <SafeIcon icon={FiArrowRight} />
          </a>
        </div>

        <div className="hero-note">
          <span className="note-sparkle">✳</span> Made for curious readers, classrooms & big imaginations
        </div>
      </div>

      <div className="hero-art">
        <div className="hero-art-ring" />
        <span className="hero-doodle hero-doodle-one">✦</span>
        <span className="hero-doodle hero-doodle-two">✳</span>
        <img
          src={`${base}Binjamin_Dumpster_Header1.png`}
          alt="Illustrated artwork of Binjamin the Dumpster"
        />
        <div className="hero-sticker">
          WHAT COULD<br />BE? <span>↗</span>
        </div>
      </div>
    </section>
  );
}

function FeaturedBookBlurb() {
  return (
    <section className="blurb-section section-wrap" id="blurb">
      <div className="blurb-card">
        <div className="blurb-grid">
          <div className="blurb-visual">
            <div className="blurb-img-backdrop">
              <img
                src={`${base}Binjamin_Color_1.png`}
                alt="Binjamin the Dumpster smiling with arms wide open"
                loading="lazy"
              />
            </div>
            <div className="blurb-badge">
              <SafeIcon icon={FiBookOpen} className="blurb-badge-icon" />
              <span>Book One of the Series</span>
            </div>
          </div>

          <div className="blurb-content">
            <span className="section-kicker">OFFICIAL BOOK BLURB</span>
            <h2>
              What happens when <em>“what could be”</em> gets too loud to ignore?
            </h2>

            <div className="blurb-text">
              <p className="lead-paragraph">
                <strong>Binjamin The Dumpster</strong> is a story about a dumpster with a simple purpose and a fantastic dream.
              </p>
              <p>
                What happens when the question of <em>"what could be"</em> gets so loud that it can no longer be ignored? Binjamin looks to find an answer! Come along and join the journey as he embarks on an adventure to discover something more incredible than he ever expected.
              </p>
              <div className="blurb-highlight">
                <SafeIcon icon={FiCheckCircle} className="highlight-icon" />
                <p>
                  <strong>"Binjamin the Dumpster"</strong> is the first in David Smith's series of books that cover the concepts of finding your life purpose, overcoming trauma, and adjusting to life's twists and turns.
                </p>
              </div>
            </div>

            <div className="blurb-cta-row">
              <a
                className="button button-dark"
                href={amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Your Copy on Amazon <SafeIcon icon={FiArrowUpRight} />
              </a>
              <span className="edition-note">Available now in Paperback</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SeriesThemes() {
  return (
    <section className="themes-section" id="themes">
      <div className="section-wrap">
        <div className="section-intro">
          <div>
            <span className="section-kicker">02 / CORE THEMES</span>
            <h2>Big life lessons, <em>playfully told.</em></h2>
          </div>
          <p>
            Stories designed to give parents, educators, and children meaningful talking points wrapped in joyful adventures.
          </p>
        </div>

        <div className="themes-grid">
          {coreThemes.map((theme, idx) => (
            <motion.div
              key={theme.title}
              className="theme-card"
              whileHover={{ y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <div className="theme-icon-wrap">
                <SafeIcon icon={theme.icon} />
              </div>
              <span className="theme-number">0{idx + 1}</span>
              <h3>{theme.title}</h3>
              <p>{theme.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Books() {
  return (
    <section className="books section-wrap" id="books">
      <div className="section-intro">
        <div>
          <span className="section-kicker">03 / THE BOOKS</span>
          <h2>Stories to <em>get lost in.</em></h2>
        </div>
        <p>Two unforgettable titles from the imaginative world of David Smith.</p>
      </div>

      <div className="book-grid">
        {/* Book 1: Binjamin */}
        <article className="book-card featured-card">
          <div className="book-image featured-image">
            <span className="image-label">NOW AVAILABLE</span>
            <img
              src={`${base}Binjamin_Dumpster1.png`}
              alt="Binjamin the Dumpster book artwork"
              loading="lazy"
            />
            <span className="image-star">✳</span>
          </div>
          <div className="book-info">
            <div>
              <span className="book-overline">BOOK 1 • AVAILABLE ON AMAZON</span>
              <h3>Binjamin the<br />Dumpster</h3>
              <p>A curious dumpster leaves behind ordinary routines to seek answers, purpose, and boundless adventure.</p>
            </div>
            <a
              className="circle-link"
              href={amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Shop Binjamin the Dumpster on Amazon"
            >
              <SafeIcon icon={FiArrowUpRight} />
            </a>
          </div>
        </article>

        {/* Book 2: Pepito */}
        <article className="book-card pepito-card">
          <div className="book-image pepito-cover-wrap">
            <img
              src={pepitoImages.cover}
              alt="Pepito the Hermit Crab book cover by David Smith"
              className="pepito-real-cover"
              loading="lazy"
            />
            <div className="pepito-floating-badge">
              <span>🦀 FEATURED TITLE</span>
            </div>
          </div>
          <div className="book-info">
            <div>
              <span className="book-overline">BY DAVID SMITH</span>
              <h3>Pepito the<br />Hermit Crab</h3>
              <p>
                Follow Pepito on his captivating expedition navigating buildings, brick walls, and mysterious pipes to find his place in a big world.
              </p>
            </div>
            <span className="book-decor"><SafeIcon icon={FiStar} /></span>
          </div>
        </article>
      </div>
    </section>
  );
}

function Illustrations() {
  return (
    <section className="illustrations" id="illustrations">
      <div className="section-wrap">
        <div className="section-intro">
          <div>
            <span className="section-kicker">05 / BEHIND THE PAGES</span>
            <h2>A world full of <em>character & expression.</em></h2>
          </div>
          <p>Explore original artwork from both Binjamin the Dumpster and Pepito the Hermit Crab.</p>
        </div>

        <ArtworkGallery />

        {/* Faces Strip for Binjamin */}
        <div className="faces-strip">
          <div className="faces-title">
            <span>THE MANY MOODS OF BINJAMIN</span>
          </div>
          <div className="faces-row">
            {characterFaces.map((faceUrl, i) => (
              <div
                key={i}
                className="face-bubble"
                title={`Binjamin expression ${i + 1}`}
              >
                <img
                  src={faceUrl}
                  alt={`Binjamin face emotion ${i + 1}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="about section-wrap" id="about">
      <div className="about-visual">
        <div className="about-blob" />
        <img
          src={`${base}Binjamin_Dumpster2.png`}
          alt="Artwork from David Smith’s Binjamin the Dumpster"
          loading="lazy"
        />
        <span className="about-asterisk">✳</span>
      </div>

      <div className="about-copy">
        <span className="section-kicker">06 / MEET THE AUTHOR</span>
        <h2>Hi, I’m<br /><em>David Smith.</em></h2>
        <p>
          I write children’s books that bring comfort, laughter, and courage to storytime. Through <strong>Binjamin the Dumpster</strong> and <strong>Pepito the Hermit Crab</strong>, I hope to inspire young readers to question their limits, embrace changes, and find their unique path forward.
        </p>
        <p>
          Whether reading together before bed or in a bustling classroom, these books are created to inspire heartfelt conversations about life's challenges, growth, and discoveries.
        </p>
        <a
          className="button button-dark"
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Discover Binjamin on Amazon <SafeIcon icon={FiArrowUpRight} />
        </a>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner section-wrap">
        <div>
          <span className="footer-kicker">THE ADVENTURE BEGINS</span>
          <h2>Ready to meet<br /><em>Binjamin & Pepito?</em></h2>
          <p className="footer-sub">
            Pick up your copy today and join unforgettable characters on their journeys of curiosity and courage.
          </p>
        </div>
        <a
          className="button button-light"
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Order on Amazon <SafeIcon icon={FiArrowUpRight} />
        </a>
      </div>

      <div className="footer-bottom section-wrap">
        <span>© {new Date().getFullYear()} David Smith. All rights reserved.</span>
        <span>Stories of purpose, resilience & imagination ✳</span>
        <a href="#home">Back to top ↑</a>
      </div>
    </footer>
  );
}

export default function App() {
  useEffect(() => {
    document.title = 'David Smith | Binjamin the Dumpster & Pepito the Hermit Crab';
  }, []);

  return (
    <>
      <Header />
      <main>
        <Hero />
        <FeaturedBookBlurb />
        <SeriesThemes />
        <Books />
        <ClassroomGuide />
        <Illustrations />
        <About />
      </main>
      <Footer />
    </>
  );
}
